import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { X, Search, Plus, Loader2, RotateCcw } from "lucide-react";

/**
 * FeatureToggleModal — the single admin surface for showing/hiding nav items.
 * Reused by the sidebar bottom "+ Add features" pill, the per-group "+" button,
 * and the /features admin page (which just embeds this as a full-page card).
 *
 * Props:
 *   navGroups       [{ label, items:[{to,label,testid,icon}] }] — flat metadata
 *   scopeGroup      optional group label to limit toggles to a single group
 *   onClose()
 *   onChange(hiddenSet: string[])  — called after save
 */
export function FeatureToggleModal({ navGroups, scopeGroup, onClose, onChange }) {
  const [hidden, setHidden] = useState(new Set());
  const [orgHidden, setOrgHidden] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/app-settings/hidden-features")
      .then((r) => {
        setHidden(new Set(r.data?.user || []));
        setOrgHidden(new Set(r.data?.org || []));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return navGroups
      .filter((g) => !scopeGroup || g.label === scopeGroup)
      .map((g) => ({
        ...g,
        items: g.items.filter((it) => !q || it.label.toLowerCase().includes(q)),
      }))
      .filter((g) => g.items.length > 0);
  }, [navGroups, scopeGroup, query]);

  const toggle = (testid) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(testid)) next.delete(testid);
      else next.add(testid);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const arr = Array.from(hidden);
      await api.post("/app-settings/hidden-features", { hidden: arr });
      onChange && onChange(arr);
      toast.success("Sidebar updated.");
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setHidden(new Set());

  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="feature-toggle-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-slate-950 border border-white/10 rounded-2xl w-full max-w-2xl overflow-hidden max-h-[85vh] flex flex-col"
      >
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">Sidebar features</div>
            <h3 className="font-display text-lg font-bold text-white mt-0.5">
              {scopeGroup ? `Add features to "${scopeGroup}"` : "Hide or show sidebar features"}
            </h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-3 border-b border-white/5 flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search features…"
              data-testid="feature-toggle-search"
              className="w-full bg-white/[0.03] border border-white/10 rounded-md pl-7 pr-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-cyan-400/40"
            />
          </div>
          <button onClick={reset} className="text-[10px] uppercase tracking-widest text-slate-400 hover:text-cyan-300 inline-flex items-center gap-1">
            <RotateCcw className="w-3 h-3" /> Show all
          </button>
        </div>
        <div className="px-6 py-4 overflow-y-auto space-y-4 flex-1">
          {loading ? (
            <div className="text-xs text-slate-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
          ) : groups.length === 0 ? (
            <div className="text-xs text-slate-500 text-center py-8">No features match your search.</div>
          ) : (
            groups.map((g) => (
              <div key={g.label} data-testid={`feature-toggle-group-${g.label.toLowerCase()}`}>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">{g.label}</div>
                <div className="grid sm:grid-cols-2 gap-1.5">
                  {g.items.map((it) => {
                    const isHidden = hidden.has(it.testid);
                    const isOrgHidden = orgHidden.has(it.testid);
                    return (
                      <label
                        key={it.testid}
                        data-testid={`feature-toggle-item-${it.testid}`}
                        className={`flex items-center gap-2 px-2.5 py-2 rounded-md border cursor-pointer text-sm transition-colors ${
                          isHidden
                            ? "border-white/10 bg-white/[0.01] text-slate-500"
                            : "border-cyan-400/30 bg-cyan-400/[0.04] text-slate-200 hover:border-cyan-400/50"
                        }`}
                        title={isOrgHidden ? "Hidden org-wide by admin" : undefined}
                      >
                        <input
                          type="checkbox"
                          checked={!isHidden}
                          onChange={() => toggle(it.testid)}
                          className="accent-cyan-400"
                        />
                        <it.icon className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate flex-1">{it.label}</span>
                        {isOrgHidden && (
                          <span className="text-[9px] uppercase tracking-wider text-amber-300">Org</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
        <div className="px-6 py-3 border-t border-white/10 flex items-center justify-between gap-3 shrink-0">
          <div className="text-[11px] text-slate-500">
            {hidden.size} hidden · {navGroups.flatMap((g) => g.items).length - hidden.size} visible
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="text-sm text-slate-400 hover:text-white px-3 py-2">Cancel</button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="feature-toggle-save-btn"
              className="text-sm px-4 py-2 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Save changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
